import { useState } from 'react';
import { ChevronRight } from 'lucide-react';

export interface JsonSchemaNode {
  type?: string;
  description?: string;
  properties?: Record<string, JsonSchemaNode>;
  items?: JsonSchemaNode;
  required?: string[];
  anyOf?: Array<{ required?: string[] }>;
  /** Allowed values, when the tool constrains the input to a fixed set. */
  enum?: string[];
}

function humanize(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  string: { bg: 'var(--color-blue-100)', text: 'var(--color-blue-800)' },
  number: { bg: 'var(--color-amber-100)', text: 'var(--color-amber-800)' },
  boolean: { bg: 'var(--color-violet-100)', text: 'var(--color-violet-800)' },
  array: { bg: 'var(--color-green-100)', text: 'var(--color-green-800)' },
  object: { bg: 'var(--color-purple-100)', text: 'var(--color-purple-800)' },
};

function TypeBadge({ type }: { type: string }) {
  const colors = TYPE_COLORS[type] ?? { bg: 'var(--color-gray-100)', text: 'var(--color-text-prose)' };
  return (
    <span
      className="inline-block rounded px-1 py-px text-[9px] font-semibold uppercase leading-tight tracking-wide"
      style={{ background: colors.bg, color: colors.text }}
    >
      {type === 'array' ? 'array' : type}
    </span>
  );
}

function RequiredDot() {
  return (
    <span
      className="inline-block h-1 w-1 rounded-full bg-red-400 shrink-0"
      title="Required"
    />
  );
}

interface PropertyRowProps {
  name: string;
  node: JsonSchemaNode;
  isRequired: boolean;
  depth: number;
}

function PropertyRow({ name, node, isRequired, depth }: PropertyRowProps) {
  const hasChildren = !!(
    (node.type === 'object' && node.properties && Object.keys(node.properties).length > 0) ||
    (node.type === 'array' && node.items?.properties && Object.keys(node.items.properties).length > 0)
  );
  const [expanded, setExpanded] = useState(depth < 1);

  const childProperties =
    node.type === 'array' && node.items?.properties
      ? node.items.properties
      : node.properties;

  const childRequired =
    node.type === 'array' && node.items?.required
      ? node.items.required
      : node.required;

  const displayType = node.type === 'array' ? `${node.items?.type ?? 'item'}[]` : (node.type ?? 'any');

  return (
    <div>
      <div
        className="flex items-start gap-1.5 group"
        style={{ paddingLeft: depth * 16 }}
      >
        {hasChildren ? (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-px shrink-0 text-gray-400 hover:text-gray-600 transition-colors p-0 bg-transparent border-none cursor-pointer"
          >
            <ChevronRight
              size={12}
              className="transition-transform duration-150"
              style={{ transform: expanded ? 'rotate(90deg)' : undefined }}
            />
          </button>
        ) : (
          <span className="w-3 shrink-0" />
        )}

        <div className="flex items-center gap-1.5 min-w-0 shrink-0">
          <span className="text-[11px] font-semibold text-gray-900 whitespace-nowrap">
            {humanize(name)}
          </span>
          {isRequired && <RequiredDot />}
          <TypeBadge type={displayType} />
        </div>

        {node.description && (
          <span className="text-[10px] text-gray-500 leading-snug break-words min-w-0">
            {node.description}
          </span>
        )}
      </div>

      {hasChildren && expanded && childProperties && (
        <div className="relative" style={{ marginLeft: depth * 16 + 6 }}>
          <div className="absolute left-0 top-0 bottom-0 w-px bg-gray-200" />
          <div className="pl-2.5 pt-0.5">
            {Object.entries(childProperties).map(([key, prop]) => (
              <PropertyRow
                key={key}
                name={key}
                node={prop}
                isRequired={childRequired?.includes(key) ?? false}
                depth={0}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AnyOfHint({ anyOf }: { anyOf: Array<{ required?: string[] }> }) {
  const requiredSets = anyOf
    .filter((s) => s.required && s.required.length > 0)
    .map((s) => s.required!.map(humanize).join(' + '));
  if (requiredSets.length === 0) return null;

  return (
    <div className="flex items-start gap-1.5 mb-1.5 px-1">
      <span className="text-[10px] text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 font-medium leading-snug">
        Requires one of: {requiredSets.join(' or ')}
      </span>
    </div>
  );
}

interface SchemaViewerProps {
  schema: JsonSchemaNode | undefined | null;
  emptyMessage?: string;
}

function SchemaViewer({ schema, emptyMessage = 'No schema defined' }: SchemaViewerProps) {
  if (!schema || (!schema.properties && !schema.anyOf)) {
    return (
      <div className="text-[10px] text-gray-400 py-2 text-center italic">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {schema.anyOf && <AnyOfHint anyOf={schema.anyOf} />}
      {schema.properties &&
        Object.entries(schema.properties).map(([key, prop]) => (
          <PropertyRow
            key={key}
            name={key}
            node={prop}
            isRequired={schema.required?.includes(key) ?? false}
            depth={0}
          />
        ))}
    </div>
  );
}

interface SchemaTabsProps {
  inputSchema?: JsonSchemaNode | null;
  outputSchema?: JsonSchemaNode | null;
}

export function SchemaTabs({ inputSchema, outputSchema }: SchemaTabsProps) {
  const hasInput = !!(inputSchema && (inputSchema.properties || inputSchema.anyOf));
  const hasOutput = !!(outputSchema && outputSchema.properties);
  const [tab, setTab] = useState<'input' | 'output'>(hasInput ? 'input' : 'output');

  if (!hasInput && !hasOutput) return null;

  return (
    <div>
      <div className="flex border-b border-gray-100">
        {hasInput && (
          <button
            onClick={() => setTab('input')}
            className={`px-3 py-1.5 text-[11px] font-medium border-b-2 transition-colors bg-transparent cursor-pointer ${
              tab === 'input'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            Input
          </button>
        )}
        {hasOutput && (
          <button
            onClick={() => setTab('output')}
            className={`px-3 py-1.5 text-[11px] font-medium border-b-2 transition-colors bg-transparent cursor-pointer ${
              tab === 'output'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            Output
          </button>
        )}
      </div>
      <div className="p-2.5 max-h-[260px] overflow-y-auto">
        {tab === 'input' && <SchemaViewer schema={inputSchema} emptyMessage="No input required" />}
        {tab === 'output' && <SchemaViewer schema={outputSchema} emptyMessage="No output defined" />}
      </div>
    </div>
  );
}
